/**
 * "Kit formatter" for the live template preview (UICC-001 §3: "Body editing
 * uses the kit formatter for a live preview — money, dates rendered per the
 * chosen locale"). Mirrors sk-notification-api's src/templates/money.ts
 * approach (Intl-based, lakh/crore grouping for INR) rather than inventing
 * a different convention for the frontend half of the same feature.
 */

const SYMBOL_TO_CODE: Record<string, string> = { '₹': 'INR', '$': 'USD', '€': 'EUR', '£': 'GBP' };

function codeFor(currency: string): string | null {
  const raw = currency.trim();
  if (/^[A-Za-z]{3}$/.test(raw)) return raw.toUpperCase();
  return SYMBOL_TO_CODE[raw] ?? null;
}

function intlLocaleFor(consoleLocale: string, currency?: string): string {
  if (currency && codeFor(currency) === 'INR') return 'en-IN';
  // ar-AE renders Arabic-Indic digits by default in some engines, which would
  // defeat the LTR-isolation this preview also does — pin to a Latin-digit
  // Arabic locale variant for numbers specifically.
  if (/^ar/i.test(consoleLocale)) return 'ar-u-nu-latn';
  return consoleLocale || 'en';
}

/** A placeholder value that LOOKS like money, formatted per the console's locale. */
export function previewMoney(value: number, consoleLocale: string, currency = 'INR'): string {
  const code = codeFor(currency);
  try {
    return new Intl.NumberFormat(intlLocaleFor(consoleLocale, currency), {
      style: code ? 'currency' : 'decimal',
      currency: code ?? undefined,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return String(value);
  }
}

/** A placeholder value that looks like a date, formatted per the console's locale. */
export function previewDate(iso: string, consoleLocale: string): string {
  try {
    return new Intl.DateTimeFormat(intlLocaleFor(consoleLocale), {
      day: 'numeric', month: 'short', year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/** A plausible sample value for a placeholder name, so the preview reads naturally instead of showing "{amount}" literally. */
export function sampleValueFor(placeholderKey: string, consoleLocale: string): string {
  const key = placeholderKey.toLowerCase();
  if (/(amount|price|total|balance|due)/.test(key)) return previewMoney(4200, consoleLocale);
  if (/(date|day|time)/.test(key)) return previewDate(new Date().toISOString(), consoleLocale);
  if (/(percent|discount|off)/.test(key)) return '20%';
  if (/(phone|mobile)/.test(key)) return '+919876543210';
  if (/(name)/.test(key)) return 'Deepak Kumar';
  return `{${placeholderKey}}`;
}
